/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useState } from 'react'
import '../style/fonts.scss';
import '../style/global.scss';
import type { AppProps } from 'next/app';
import PlayerStateProvider from '../contexts/PlayerStateContext';
import WebcastProvider from '../contexts/WebcastContext';
import ChannelProvider from '../contexts/ChannelContext';
import SummitProvider from '../contexts/SummitContext'
import ProfileProvider from '../contexts/ProfileContext'
import PlayerUserDataProvider from '../contexts/PlayerUserDataContext'
import WebcastCommunitiesProvider from '../contexts/WebcastCommunitiesContext';
import EnvVariableProvider, { defaultState } from '../contexts/EnvVariablesContext'
import { SWRConfig } from 'swr';
import fetcher from '../libs/fetcher';
import setCookies from '../libs/setCookies';
import useCheckPageState from '../libs/useCheckPageState';
import ChannelFeedProvider from '../contexts/ChannelFeedContext';
import { pushGroupEventToDataLayer } from '@bt-react/libs/pushGroupEventToDataLayer';
import moment from 'moment-timezone'
import Head from 'next/head'
import { useRouter } from 'next/router'
import getCanonicalUrl from '../libs/getCanonicalUrl'
import useGigIDs from '../libs/useGigIDs'
import getGigIDsFromAsPath from '../libs/getGigIDsFromAsPath/getGigIDsFromAsPath'
import axios from 'axios'
import isServerSide from '@bt-react/libs/isServerSide'
import logger from '../libs/logger'
import { UserProfileResponse } from '../libs/fetchProfile';
import fetchAuthStatus from '../libs/fetchAuthStatus'
import InformaBanner from '@bt-react/components/InformaBanner'


axios.interceptors.request.use(async (request) => {

  const logdata = {
    URL: request?.url || '',
    PARAMS: request?.params || '',
    HEADEARS: request?.headers || ''
  }

  if (isServerSide) {
    logger.debug(JSON.stringify(logdata))
  }

  return request

}, error => {

  return Promise.reject(error)

})

axios.interceptors.response.use(function (response) {

  const logdata = {
    URL: response?.config?.url || '',
    RESPONSE: response?.data || '',
    HEADEARS: response?.headers || ''
  }

  if (isServerSide) {
    logger.debug(JSON.stringify(logdata))
  }

  return response

}, error => {

  if (isServerSide) {
    logger.error(error)
  }

  return Promise.reject(error)

})

function MyApp({ Component, pageProps, err }: AppProps & { err: any }) {

  const [profileData, setProfileData] = useState<UserProfileResponse | null>(null);
  const [isSessionCookiePresent, setIsSessionCookiePresent] = useState(false)

  let dateString = ''

  const { asPath } = useRouter()
  
  useEffect(() => {

    (async () => {

      const data = await fetchAuthStatus(pageProps?.envVariables.hostUrl)

      setIsSessionCookiePresent(data?.isSessionCookiePresent || false)

    })()

  }, [])

  useEffect(() => {

    if (profileData) {

      if (pageProps?.['webcast']?.['start']) {
        const date = moment(pageProps['webcast']['start'])
        dateString = date.format('MM-DD-YYYY, HH:mm:ss a')
      }
      pushGroupEventToDataLayer('ga4_config_events', {
        siteName: 'BrightTALK',
        channel_name: pageProps?.['channel']?.['channelName'] ? pageProps['channel']['channelName'] : '',
        community_parent: pageProps?.['webcastCommunities']?.['communities']
          ? pageProps['webcastCommunities']['communities']
          : 'None',
        contentID: pageProps?.['webcastId'] ? pageProps['webcastId'] : '',
        contentType: 'webcast',
        content_subtype: '',
        topicName: pageProps?.['webcast']?.['topics']
          ? pageProps['webcast']['topics']
          : 'None',
        parentTopicName: pageProps?.['webcastCommunities']?.['communities']
          ? pageProps['webcastCommunities']['communities']
          : 'None',
        publicationDate: dateString,
        contentGroup: '',
      });
  
      pushGroupEventToDataLayer('content_conversion', {
        method: '',
        type: pageProps?.['webcast'] ? pageProps['webcast'] : ''
      })
  
      pushGroupEventToDataLayer('subscription_conversion', {
        method: '',
        type: pageProps?.['webcast'] ? pageProps['webcast'] : ''
      })
  
      let memberAge = '', memberStatus = '';
     if (profileData?.isLoggedIn) {
        memberStatus = 'MEMBER'
      } else {
        memberStatus = 'NOT_MEMBER'
      }
  
      if (profileData?.created) {
        const dateCreated = moment(profileData?.created)
        memberAge = dateCreated.format('MM-DD-YYYY, HH:mm:ss a')
      } else {
        memberAge = ''
      }

      pushGroupEventToDataLayer('ga4_config_events', {
        'siteName': 'BrightTALK',
        'memberStatus': memberStatus,
        'memberAge': memberAge,
        'pageType': 'webcast',
        'webinar_duration': pageProps?.webcast?.duration || '',
      })
    }

  }, [profileData]);

  const updateData = (data: UserProfileResponse) => {
    setProfileData(data)
  }

  setCookies();
  const initialWebcastDetails = pageProps?.webcast;
  useCheckPageState(initialWebcastDetails, pageProps?.envVariables?.hostUrl);

  const defaultRouterVars = getGigIDsFromAsPath(asPath)
  const {
    channelId = defaultRouterVars.channelId,
    webcastId = defaultRouterVars.webcastId
  } = useGigIDs()
  const canonicalUrl = getCanonicalUrl(pageProps?.envVariables?.hostUrl, channelId as string, webcastId as string)

  return (
    <EnvVariableProvider value={pageProps?.envVariables || defaultState}>
      <SWRConfig value={{ fetcher }}>
        <ChannelProvider value={pageProps?.channel}>
          <WebcastProvider value={initialWebcastDetails}>
            <SummitProvider value={pageProps?.summit}>
              <PlayerUserDataProvider>
                <ProfileProvider updateData={updateData}>
                  <PlayerStateProvider>
                    <WebcastCommunitiesProvider
                      value={pageProps?.webcastCommunities}
                    >
                      <ChannelFeedProvider value={pageProps?.channelFeed}>
                        <Head>
                          <link rel='icon' type='image/png' href={`${pageProps?.envVariables?.staticAssetCdnUrl || process.env.STATIC_ASSETS_CDN_URL}/images/favicon.ico?nocache=1`} />
                          <link rel='apple-touch-icon' href={`${pageProps?.envVariables?.staticAssetCdnUrl || process.env.STATIC_ASSETS_CDN_URL}/images/favicon-apple-touch-icon.png?nocache=1`} />
                          <link rel='canonical' href={canonicalUrl} />
                        </Head>
                        <InformaBanner />
                        <Component {...pageProps} isSessionCookiePresent={isSessionCookiePresent} err={err} />
                      </ChannelFeedProvider>
                    </WebcastCommunitiesProvider>
                  </PlayerStateProvider>
                </ProfileProvider>
              </PlayerUserDataProvider>
            </SummitProvider>
          </WebcastProvider>
        </ChannelProvider>
      </SWRConfig>
    </EnvVariableProvider>
  );
}

export default MyApp;
